// models
import { makeStyles } from '@fluentui/react-components';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { ErrorType } from '../../../models/HttpError';

// stores
import * as ContractStore from '../../../redux/middleware/ContractMiddleware'
import * as ContractStageStore from '../../../redux/middleware/ContractStageMiddleware'
import * as ProtocolsStore from '../../../redux/middleware/MedicalProtocolMiddleware'
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware'
import * as ProtocolPractitionersStore from '../../../redux/middleware/ProtocolPractitionersMiddleware'

// state
import { AppState } from '../../../redux/reducers';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { ProtocolVisit } from '../../../models/Entities/Protocols/ProtocolVisit';
import { HcProtocolPatient } from '../../../models/Entities/PractitionerAssociations/HcProtocolPatient';

//#region Props

interface IProps {
    ProtocolId: number;
}

interface ConnectedProps {
    IsLoadingProtocol: boolean;
    LoadedProtocolSuccessfully: boolean;
    ErrorLoadingProtocol: ErrorType | undefined;
    IsLoadingContract: boolean;
    LoadedContractSuccessfully: boolean;
    ErrorLoadingContract: ErrorType | undefined;
    ContractId: number | null;
    Protocol: Protocol | null;
    Contract: Contract | null;
    ProtocolPatients: ProtocolPatient[];
    Stages: ContractStage[];
    PatientVisits: PatientVisit[];
    Visits: ProtocolVisit[];
    GetPractitioner: (id: string) => Practitioner | undefined;
    Patients: string[];
    HcProtocolPatients: HcProtocolPatient[];
    IsLoadingHcProtocolPatients: boolean;
    IsGettingVisits: boolean;    
}

interface MedicalServicesDispatchProps {
    GetMedicalProtocolById: typeof ProtocolsStore.actionCreators.GetMedicalProtocolById;
    GetProtocolPatients: typeof ProtocolsStore.actionCreators.GetProtocolPatients;
    GetContractById: typeof ContractStore.actionCreators.GetContractById;
    GetAllContractsStage: typeof ContractStageStore.actionCreators.GetAllContractsStage;
    GetVisitsByProtocolId: typeof ProtocolsStore.actionCreators.GetVisitsByProtocolId;
    GetPatientVisitByProtocolId: typeof ProtocolsStore.actionCreators.GetPatientVisitByProtocolId;
    GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
    GetAllProtocolContractAssociations: typeof ProtocolsStore.actionCreators.GetAllProtocolContractAssociations;
    GetHcProtocolPatientsByProtocolId: typeof ProtocolPractitionersStore.actionCreators.GetHcProtocolPatientsByProtocolId;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const AuxContractId: number | null =  state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.contractAssociation?.contractId || null;

    const AuxContract: Contract | null = state.Contract?.list?.find((contract: Contract) => contract?.id === AuxContractId) || null;

    const AuxProtocol: Protocol | null = state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId) || null;

    const AuxPatients: string[] | null = AuxProtocol?.patientVisits?.map((visit: PatientVisit) => visit.numberInProtocol).reduce(
        (acc: string[], current: string) => {
            if(!acc.includes(current)) {
                acc.push(current);
            }
            return acc;
        }, []
    ) || [];

    const AuxProtocolPatients: ProtocolPatient[] | null = state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.patients || null;

    var AuxContractStages: ContractStage[] = [];

    if(AuxContract) {
        AuxContractStages = state.Contract?.list?.find((contract: Contract) => contract?.id === AuxContractId)?.stages || [];
    }

    return {
        IsLoadingProtocol: state.MedicalProtocols?.isLoadingOne,
        LoadedProtocolSuccessfully: state.MedicalProtocols?.successLoadingOne,
        ErrorLoadingProtocol: state.MedicalProtocols?.error,
        IsLoadingContract: state.Contract?.isLoadingOne,
        LoadedContractSuccessfully: state.Contract?.successLoadingOne,
        ErrorLoadingContract: state.Contract?.error,
        Protocol: AuxProtocol || null,
        ContractId: AuxContractId,
        Contract: AuxContract,
        ProtocolPatients: state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.patients?.sort(
            (a: ProtocolPatient, b: ProtocolPatient) => a.numberInProtocol.localeCompare(b.numberInProtocol)
        ) || [],
        Stages: AuxContractStages,
        PatientVisits: state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.patientVisits || [],
        Visits: state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.visits || [],
        GetPractitioner: (id: string) => { 
            var practitioner = state.Practitioners?.list?.find((practitioner: Practitioner) => practitioner.userId === id);
            return practitioner;
        },
        Patients: AuxPatients,
        HcProtocolPatients: state.ProtocolPractitioners?.hcProtocolPatients || [],
        IsLoadingHcProtocolPatients: state.ProtocolPractitioners?.isLoadingHcProtocolPatients || false,
        IsGettingVisits: state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.isGettingVisits || false
    }
};

export const mapDispatchToProps = {
    ...ContractStore.actionCreators,
    ...ProtocolsStore.actionCreators,
    ...ContractStageStore.actionCreators,
    ...PractitionerStore.actionCreators,
    ...ProtocolPractitionersStore.actionCreators
};


// component functions

export interface internalStage {
    stageId: number | null;
    stageName: string;
    flowchartName?: string;
    order: number;
    crossVersionId?: number;
    isRemote: boolean;
    remoteWarning: boolean;
}

export const GetRealStages = (stages: ContractStage[], visist: ProtocolVisit[]): internalStage[]  => {

    let realStages: internalStage[] = [];

    // convert stages to internalStage
    let stagesAsInternalStage: internalStage[] = stages.sort(
        (a: ContractStage, b: ContractStage) => a.stageOrder - b.stageOrder
    ).map((stage: ContractStage, index) => {
        return {stageId: stage.id, stageName: stage.stageName, flowchartName: "", order: index, crossVersionId: stage.emrProtocolVisitIdRelated, isRemote: stage.isOnSiteMandatory ? false : true, remoteWarning: false} as internalStage;
    });

    console.log(stagesAsInternalStage);

    // convert visits to internalStage
    let visitsAsInternalStage: internalStage[] = visist.sort(
        (a: ProtocolVisit, b: ProtocolVisit) => a.order - b.order
    ).map((visit: ProtocolVisit, index) => {
        return {stageId: null, stageName: visit.title, flowchartName: visit.title, order: index, crossVersionId: visit.crossVersionId, isRemote: visit.type === 0 ? false : true, remoteWarning: false} as internalStage;
    });

    console.log(visitsAsInternalStage);

    realStages = [...stagesAsInternalStage];

    let FinishStages: internalStage[] = [];
    
    realStages.forEach((visit: internalStage) => {
        let foundStage = visitsAsInternalStage.find((stage: internalStage) => stage.crossVersionId === visit.crossVersionId);
        if(foundStage) {
            const currentVisit = {...visit, flowchartName: foundStage.stageName, crossVersionId: foundStage.crossVersionId, isRemote: visit.isRemote, remoteWarning: visit.isRemote && !foundStage.isRemote};
            FinishStages.push(currentVisit);
            //visitsAsInternalStage = visitsAsInternalStage.filter((stage: internalStage) => stage.crossVersionId !== visit.crossVersionId);
        } else {
            foundStage = visitsAsInternalStage.find((stage: internalStage) => CompareVisitsNames(visit.stageName, stage.stageName));
            if(foundStage) {
                const currentVisit = {...visit, flowchartName: foundStage.stageName, crossVersionId: foundStage.crossVersionId, isRemote: visit.isRemote, remoteWarning: visit.isRemote && !foundStage.isRemote};
                FinishStages.push(currentVisit);
                //visitsAsInternalStage = visitsAsInternalStage.filter((stage: internalStage) => stage.order !== visit.order);
            } else {
                FinishStages.push(visit); 
            }
        }
    });

    FinishStages = [...FinishStages, ...visitsAsInternalStage.filter((visit: internalStage) => !FinishStages.some((stage: internalStage) => stage.crossVersionId === visit.crossVersionId))];

    console.log(FinishStages);

    return FinishStages.sort((a: internalStage, b: internalStage) => a.order - b.order);
}

// check if a visit is realized
export const IsVisitExist = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): boolean => {
    return visits.some((visit: PatientVisit) => visit.numberInProtocol.toLowerCase() === patientNumberInProtocol.toLowerCase() && visit.crossVersionId === stage.crossVersionId);
}

// return visit status
export const GetVisitStatus = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): string => {
    const visit = visits.find((visit: PatientVisit) => 
        visit.numberInProtocol.toLowerCase() === patientNumberInProtocol.toLowerCase() && 
        visit.crossVersionId === stage.crossVersionId);
    if(visit !== undefined) {
        return GetVisitStatusByVisit(visit.status);
    } else {
        return '';
    }
}

// get visit status by visit
export const GetVisitStatusByVisit = (visitStatus: number): string => {
    switch(visitStatus) {
        case 0: return 'Started';
        case 1: return 'Completed';
        case 2: return 'Skipped';
        case 3: return 'Failed';
        case 4: return 'Changed';
        case 5: return 'AutoCompleted';
        default: return 'Not Realized';
    }
}

export const GetVisitStatusDescription = (status: number): string => {
    switch(status) {
        case 0: return 'Iniciada';
        case 1: return 'Completada';
        case 2: return 'Salteada';
        case 3: return 'Falla de selección';
        case 4: return 'Modificada';
        case 5: return 'Autocompletada';
        default: return 'No realizada';
    }
}

export const getVisit = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): PatientVisit | undefined => {
    return visits.find((visit: PatientVisit) => visit.numberInProtocol.toLowerCase() === patientNumberInProtocol.toLowerCase() && visit.order === stage.order);
}

// get visit by patient and stage
export const GetVisitByPatientAndStage = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): PatientVisit | null => {
    return visits.find((visit: PatientVisit) => visit.numberInProtocol.toLowerCase() === patientNumberInProtocol.toLowerCase() && visit.crossVersionId === stage.crossVersionId) || null;
}

// get first character of a string
export const GetFirstCharacter = (str: string): string => {
    return str.substring(0, 1).toLowerCase();
}

// get number of a string
export const GetNumber = (str: string): number => {
    return parseInt(str.replace(/\D/g, ''));
}

export const CompareVisitsNames = (stageName: string, visitName: string): boolean => {
    const stageInitial = GetFirstCharacter(stageName);
    const visitInitial = GetFirstCharacter(visitName);

    const stageNumber = GetNumber(stageName);
    const visitNumber = GetNumber(visitName);

    return stageInitial === visitInitial && stageNumber === visitNumber;
};

// styles
export const useStyles = makeStyles({
    contentHeader: {
      marginTop: "0",
    },
});